/**
 * Past election, typed by hand for one booth (duty "पिछला चुनाव का विवरण भरना"): the candidates of
 * the assembly's last election, the booth's votes for each. Saved straight to the API (online).
 * Counts only (28 Oct 2026): once a booth's result is saved the phone says so and offers no form;
 * the votes never come back to the phone and corrections are made on the web.
 */
import { useEffect, useState } from "react";
import { apiErrorText } from "@core/messages";
import type { BoothResultReply } from "../field/api";
import { useApp, useQuery, useSession } from "../ui/context";
import { navigate } from "../ui/router";
import { Banner, Button, NumberField } from "../ui/components";

export function ResultScreen({ code }: { code: string }) {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const [r, setR] = useState<BoothResultReply | null>(null);
  const [votes, setVotes] = useState<Record<number, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [noElection, setNoElection] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [tries, setTries] = useState(0);
  const booth = useQuery(async () => (assemblyId ? app.core.store.getBooth(assemblyId, code) : null), [assemblyId, code], ["booths"]);

  useEffect(() => {
    let off = false;
    setErr(null);
    app.online((api, aid) => api.boothResult(aid, code)).then(
      (x) => {
        if (off) return;
        setR(x);
        setVotes(Object.fromEntries(x.candidates.map((c) => [c.id, c.votes == null ? "" : String(c.votes)])));
      },
      (e) => {
        if (off) return;
        if ((e as { code?: string })?.code === "no_election") setNoElection(true);
        else setErr((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : t.resNeedsNet);
      }
    );
    return () => {
      off = true;
    };
  }, [app, code, lang, t, tries]);

  const valid = (v: string) => /^\d{1,6}$/.test(v.trim());
  const filled = r ? r.candidates.filter((c) => (votes[c.id] ?? "").trim() !== "") : [];
  const bad = filled.some((c) => !valid(votes[c.id]));
  const total = filled.reduce((a, c) => a + (valid(votes[c.id]) ? Number(votes[c.id]) : 0), 0);

  const save = async () => {
    if (!filled.length) return setErr(t.resNothing);
    if (bad) return;
    setBusy(true);
    setErr(null);
    try {
      await app.online((api, aid) => api.saveBoothResult(aid, code, filled.map((c) => ({ candidateId: c.id, votes: Number(votes[c.id]) }))));
      setOk(true);
      setTimeout(() => navigate({ name: "booth", code }), 900);
    } catch (e) {
      setErr((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : t.resNeedsNet);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid="result"
    >
      <div>
        <div style={{ fontWeight: 800, color: "var(--primary)" }}>
          {t.boothNo} {code}
          {booth ? ` · ${booth.name}` : ""}
        </div>
        <h2 style={{ margin: "2px 0 4px" }}>🗳 {t.secResult}</h2>
        {r ? (
          <p className="muted" style={{ margin: 0 }}>
            {t.resExplain(r.election.year)}
          </p>
        ) : null}
      </div>
      {noElection ? <Banner kind="warn">{t.resNoElection}</Banner> : null}
      {err ? <Banner kind="error" action={!r ? { label: t.reload, onClick: () => setTries((n) => n + 1) } : undefined}>{err}</Banner> : null}
      {r?.filled && !ok ? <Banner kind="ok">{t.aoResultDone}</Banner> : null}
      {r && (!r.filled || ok) ? (
        <>
          {r.candidates.map((c) => (
            <NumberField
              key={c.id}
              label={c.isAggregate ? t.resOthers : `${c.name}${c.party ? ` (${c.party})` : ""}`}
              name={`v-${c.id}`}
              value={votes[c.id] ?? ""}
              onChange={(v) => setVotes((x) => ({ ...x, [c.id]: v }))}
              error={(votes[c.id] ?? "").trim() !== "" && !valid(votes[c.id]) ? t.required : null}
            />
          ))}
          <div className="total-line">
            {t.resTotal}: <b>{total.toLocaleString("en-IN")}</b>
          </div>
          {ok ? <Banner kind="ok">{t.resSaved}</Banner> : null}
          <Button type="submit" big busy={busy} disabled={bad} testId="result-save">
            {t.resSave}
          </Button>
        </>
      ) : !noElection && !err ? (
        <p className="muted">…</p>
      ) : null}
    </form>
  );
}
